import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthenticationUser } from '../models/authentication/authentication-user';
import { ChangePasswordRequest } from '../models/authentication/change-password-request';
import { LoginRequest } from '../models/authentication/login-request';
import { LoginResponse } from '../models/authentication/login-response';
import { ManageRolesRequest } from '../models/authentication/manage-roles-request';
import { RegisterRequest } from '../models/authentication/register-request';
import { ResetPasswordRequest } from '../models/authentication/reset-password-request';
import { UpdateRequest } from '../models/authentication/update-request';
import { User } from '../models/authentication/user';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private httpClient = inject(HttpClient);
  private controllerUrl = environment.apiBaseUrl + '/api/' + environment.apiVersion + '/account';
  private currentUserSubject: BehaviorSubject<AuthenticationUser>;
  public currentUser: Observable<AuthenticationUser>;
  private router = inject(Router);

  constructor() {
    this.currentUserSubject = new BehaviorSubject<AuthenticationUser>(JSON.parse(localStorage.getItem('currentUser')));
    this.currentUser = this.currentUserSubject.asObservable();    
  }

  public get currentUserValue(): AuthenticationUser {
    return this.currentUserSubject.value;
  }

  getAllUsers(): Observable<User[]> {
    return this.httpClient.get<User[]>(this.controllerUrl);
  }

  getUsersByRole(role: string): Observable<User[]> {
    return this.httpClient.get<User[]>(this.controllerUrl + '/role/' + role);
  }

  getUserById(id: string): Observable<User> {
    return this.httpClient.get<User>(this.controllerUrl + '/' + id);
  }

  getAllRoles(): Observable<string[]> {
    return this.httpClient.get<string[]>(this.controllerUrl + '/roles');
  }

  getUserRoles(id: string): Observable<string[]> {
    return this.httpClient.get<string[]>(this.controllerUrl + '/' + id + '/roles');
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.httpClient.post<LoginResponse>(this.controllerUrl + '/login', credentials)
    .pipe(map(response => {
      var decodedToken = jwtDecode<{ name: string, surname: string, email: string }>(response.accessToken);
      var user: AuthenticationUser = {
        name: decodedToken.name,
        surname: decodedToken.surname,
        email: decodedToken.email,
        accessToken: response.accessToken,
        refreshToken: response.refreshToken
      };

      this.currentUserSubject.next(user);
      localStorage.setItem('currentUser', JSON.stringify(user));

      return response;
    }));
  }

  register(registerRequest: RegisterRequest){
    return this.httpClient.post(this.controllerUrl + '/register', registerRequest);
  }

  changePassword(request: ChangePasswordRequest) {
    return this.httpClient.post(this.controllerUrl + '/change-password', request);
  }

  resetPassword(resetPasswordRequest: ResetPasswordRequest) {
    return this.httpClient.post(this.controllerUrl + '/reset-password', resetPasswordRequest);
  }

  confirmReset(request: LoginRequest): Observable<boolean> {
    return this.httpClient.post<boolean>(this.controllerUrl + '/confirm-reset', request);
  }

  update(request: UpdateRequest) {
    return this.httpClient.put(this.controllerUrl, request);
  }
  
  delete(id: string) {
    return this.httpClient.delete(this.controllerUrl + '/' + id);
  }
  
  refreshToken(): Observable<LoginResponse> {
    return this.httpClient.post<any>(this.controllerUrl + '/refresh-token', 
      { 
        accessToken: this.currentUserValue.accessToken, refreshToken: this.currentUserValue.refreshToken 
      }).pipe(
        map(response => {
          if (response && response.accessToken && response.refreshToken) {
            var decodedToken = jwtDecode<{ name: string, surname: string, email: string}>(response.accessToken);
            var user: AuthenticationUser = {
              name: decodedToken.name,
              surname: decodedToken.surname,
              email: decodedToken.email,
              accessToken: response.accessToken,
              refreshToken: response.refreshToken
            };
            this.currentUserSubject.next(user);
            localStorage.setItem('currentUser', JSON.stringify(user));
          }
          return response;
        })
      );
  }

  manageUserRoles(manageRolesRequest: ManageRolesRequest) {
    return this.httpClient.post(this.controllerUrl + '/manage-roles', manageRolesRequest); 
  }

  logout() {
    localStorage.removeItem('currentUser');
    this.currentUserSubject.next(null);
  }
}
